import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  TriggersTemplatePagesContainer,
  Content,
  TriggerViewHeader,
} from "./TriggerViewPage.styled";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useNavigate, Routes, Route, useParams } from "react-router-dom";
import { Header, Switcher } from "./components";
import Overview from "./pages/Overview/Overview";
import Automation from "./pages/Automation/Automation";
import Logs from "./pages/Logs/Logs";
import { useTriggers } from "../../context/TriggersContext";
import { Alert } from "@ann/web-components";
import { getTriggerById } from "../../../../../services";
import CircularProgress from '@mui/material/CircularProgress';
import { AutosaveProvider } from "../../../../../context/AutosaveContext";

const TriggerViewPage = () => {
  const navigate = useNavigate();
  const { getTriggerByIdAction } = useTriggers();
  const { triggerId } = useParams();
  
  const [trigger, setTrigger] = useState(null);
  const [alertInfo, setAlertInfo] = useState(null);
  const [isWorkflowReady, setIsWorkflowReady] = useState(false);
  const [isPollingForFlow, setIsPollingForFlow] = useState(false);
  const [formData, setFormData] = useState(null);
  const [isLoadingTrigger, setIsLoadingTrigger] = useState(false);
  
  const overviewRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const stableGetTriggerById = useCallback((id) => {
    return getTriggerByIdAction(id);
  }, [getTriggerByIdAction]);

  // Load trigger data based on triggerId
  useEffect(() => {
    if (triggerId === "new") {
      setTrigger({
        name: "New Trigger",
      });
      setFormData(null);
      setIsWorkflowReady(false);
      return; 
    }
    
    if (triggerId?.startsWith("template-")) {
      const storedName = localStorage.getItem('currentTriggerName');
      const storedCreatedBy = localStorage.getItem('currentTriggerCreatedBy');
      const storedLastUpdated = localStorage.getItem('currentTriggerLastUpdated');
      setTrigger({
        name: storedName || "Template Trigger",
        id: triggerId,
        type: "template",
        createdBy: storedCreatedBy || "Template",
        lastUpdated: storedLastUpdated || ""
      });
      setFormData(null);
      setIsWorkflowReady(false);
      return;
    }
    
    const loadTrigger = async () => {
      try {
        setIsLoadingTrigger(true);

        let response = await stableGetTriggerById(triggerId);

        if (!response) {
          const apiResponse = await getTriggerById({ triggerId });
          if (apiResponse.success && apiResponse.data) {
            response = apiResponse.data;
          }
        }

        if (response) {
          setTrigger(response);
          setFormData(null);
          const hasWorkflow = !!(response.externalFlowId);
          setIsWorkflowReady(hasWorkflow);

          if (!response.externalFlowId && response.id) {
            startPollingForExternalFlowId(response.id);
          }
        } else {
          setTrigger(null);
          setIsWorkflowReady(false);
        }
      } catch (error) {
        console.error('Error loading trigger:', error);
        setTrigger(null);
        setIsWorkflowReady(false);
      } finally {
        setIsLoadingTrigger(false);
      }
    };
    loadTrigger();
  }, [triggerId, stableGetTriggerById]);

  const startPollingForExternalFlowId = (triggerIdToCheck) => {
    if (isPollingForFlow) {
      return;
    }
    
    setIsPollingForFlow(true);
    
    let pollCount = 0;
    const maxPolls = 30;
    
    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      
        try {
            const response = await getTriggerById({ triggerId: triggerIdToCheck });
         
         if (response.success && response.data && response.data.flow?.externalFlowId) {
           const updatedTriggerData = {
             id: response.data.workflow?.workflowCatalogUuid,
             uuid: response.data.workflow?.workflowCatalogUuid,
             name: response.data.workflow?.name,
             description: response.data.workflow?.description,
             workflowType: response.data.workflow?.workflowType,
             status: response.data.workflow?.status,
             externalFlowId: response.data.flow?.externalFlowId,
             flowUuid: response.data.flow?.uuid,
             isActive: response.data.flow?.isActive,
             createdAt: response.data.workflow?.createdAt,
             updatedAt: response.data.workflow?.updatedAt,
           };
           setTrigger(updatedTriggerData);
           setIsWorkflowReady(true);
           setIsPollingForFlow(false);
           clearInterval(pollingIntervalRef.current);
           pollingIntervalRef.current = null;
         } else if (pollCount >= maxPolls) {
           try {
             const fallbackResponse = await stableGetTriggerById(triggerIdToCheck);
             if (fallbackResponse && fallbackResponse.externalFlowId) {
               setTrigger(fallbackResponse);
               setIsWorkflowReady(true);
             }
           } catch (fallbackError) {
             console.error('Fallback also failed:', fallbackError);
           }
           
           setIsPollingForFlow(false);
           clearInterval(pollingIntervalRef.current);
           pollingIntervalRef.current = null;
         }
       } catch (error) {
         console.error('Error during polling:', error);
         if (pollCount >= maxPolls) {
           setIsPollingForFlow(false);
           clearInterval(pollingIntervalRef.current);
           pollingIntervalRef.current = null;
         }
       }
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleTriggerCreated = async (newTrigger) => {
    const completeTrigger = {
      ...newTrigger,
      id: newTrigger.id || newTrigger.uuid,
      uuid: newTrigger.uuid || newTrigger.id,
      name: newTrigger.name,
      nameText: newTrigger.name,
      description: newTrigger.description || newTrigger.descriptionText || '',
      descriptionText: newTrigger.description || newTrigger.descriptionText || '',
      externalFlowId: newTrigger.externalFlowId || newTrigger.flowUuid,
      flowUuid: newTrigger.flowUuid || newTrigger.externalFlowId
    };
    
    setTrigger(completeTrigger);
    
    const alertData = localStorage.getItem("triggerPublishAlert");
    if (alertData) {
      setAlertInfo(JSON.parse(alertData));
      localStorage.removeItem("triggerPublishAlert");
    }
    
    if (completeTrigger.id && triggerId === "new") {
      navigate(`/v2/workflows/${completeTrigger.id}`, { replace: true });
    }

    // Fetch updated data from backend
    try {
      const backendResponse = await getTriggerById({ triggerId: completeTrigger.id });
      if (backendResponse.success && backendResponse.data) {
        const updatedTriggerData = {
          id: backendResponse.data.workflow?.workflowCatalogUuid,
          uuid: backendResponse.data.workflow?.workflowCatalogUuid,
          name: backendResponse.data.workflow?.name,
          description: backendResponse.data.workflow?.description,
          workflowType: backendResponse.data.workflow?.workflowType,
          status: backendResponse.data.workflow?.status,
          externalFlowId: backendResponse.data.flow?.externalFlowId,
          flowUuid: backendResponse.data.flow?.uuid,
          isActive: backendResponse.data.flow?.isActive,
          createdAt: backendResponse.data.workflow?.createdAt,
          updatedAt: backendResponse.data.workflow?.updatedAt,
        };
        
        setTrigger(updatedTriggerData);
        
        const hasWorkflow = !!(updatedTriggerData.externalFlowId);
        setIsWorkflowReady(hasWorkflow);
        
        if (!updatedTriggerData.externalFlowId && updatedTriggerData.id) {
          startPollingForExternalFlowId(updatedTriggerData.id);
        }
      }
    } catch (error) {
      const hasWorkflow = !!(completeTrigger.externalFlowId);
      setIsWorkflowReady(hasWorkflow);
      
      if (!completeTrigger.externalFlowId && completeTrigger.id) {
        startPollingForExternalFlowId(completeTrigger.id);
      }
    }
  };

  const handleTabChange = (tabId) => {
    // Save changes before switching tabs
    if (overviewRef.current?.hasUnsavedChanges()) {
      overviewRef.current?.saveImmediately();
    }
    
    if (tabId === "automation") {
      navigate(`automation`);
    } else if (tabId === "logs") {
      navigate(`logs`);
    } else {
      navigate('');
    }
  };

  // Handle when trigger is saved via autosave
  const handleTriggerSaved = (savedTriggerData) => {
    // Update the trigger state with the saved data
    setTrigger(prevTrigger => ({
      ...prevTrigger,
      ...savedTriggerData
    }));
  };

  const getFormData = () => {
    return formData || overviewRef.current?.getFormData();
  };

  const validateFormData = useCallback(() => {
    return overviewRef.current?.validateAll() || false;
  }, []);

  const isFormValid = useCallback(() => {
    return overviewRef.current?.isFormValid() || false;
  }, []);

  const hasValidationErrors = useCallback(() => {
    return overviewRef.current?.hasValidationErrors() || false;
  }, []);

  const resetForm = useCallback(() => {
    setFormData(null);
    overviewRef.current?.resetForm();
  }, []);

  const handleFormDataChange = useCallback((newFormData) => {
    setFormData(prevFormData => {
      if (JSON.stringify(prevFormData) !== JSON.stringify(newFormData)) {
        return newFormData;
      }
      return prevFormData;
    });
  }, []);

  const isNewTrigger = triggerId === "new";

  // Functions to get autosave state from Overview
  const getAutosaveState = useCallback(() => {
    if (overviewRef.current) {
      return {
        retrySave: overviewRef.current.retrySave,
        hasUnsavedChanges: overviewRef.current.hasUnsavedChanges(),
        isSaving: overviewRef.current.isSaving(),
        saveImmediately: overviewRef.current.saveImmediately,
      };
    }
    return {
      retrySave: () => {},
      hasUnsavedChanges: false,
      isSaving: false,
      saveImmediately: () => {},
    };
  }, []);

  return (
    <AutosaveProvider>
      <TriggersTemplatePagesContainer>
        <TriggerViewHeader>
          <Breadcrumb 
            triggerName={trigger?.name}
            hasUnsavedChanges={getAutosaveState().hasUnsavedChanges}
            saveImmediately={getAutosaveState().saveImmediately}
          />
          {alertInfo && (
            <Alert
              title={alertInfo.title}
              severity={alertInfo.severity}
              onClose={() => setAlertInfo(null)}
              message={alertInfo.message}
            />
          )}
          {isLoadingTrigger ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <Header 
                getFormData={getFormData} 
                validateFormData={validateFormData}
                isFormValid={isFormValid}
                hasValidationErrors={hasValidationErrors}
                onTriggerCreated={isNewTrigger ? handleTriggerCreated : undefined}
                triggerName={trigger?.name}
                {...getAutosaveState()}
              />
              <Switcher 
                onTabChange={handleTabChange} 
                isWorkflowReady={isWorkflowReady}
                triggerData={trigger}
                isPollingForFlow={isPollingForFlow}
              />
            </>
          )}
        </TriggerViewHeader>
        <Content>
          <Routes>
            <Route index element={<Overview
              ref={overviewRef}
              triggerData={trigger}
              persistentFormData={formData}
              onFormDataChange={setFormData}
              onTriggerSaved={handleTriggerSaved}
            />} />
            <Route path="automation" element={<Automation flowUuid={trigger?.externalFlowId} isPollingForFlow={isPollingForFlow} />} />
            <Route path="logs" element={<Logs />} />
          </Routes>
        </Content>
      </TriggersTemplatePagesContainer>
      
    </AutosaveProvider>
  );
};
export default TriggerViewPage;
